<template>
  <div>
    <v-card-title :class="['align-items-center', { 'top-header--manual-search': manualSearch }]">
      <div :class="manualSearch ? 'top-header-search-group' : 'top-header-search-inline'">
        <v-text-field
          v-if="filterable"
          dense
          :append-icon="manualSearch ? undefined : 'mdi-magnify'"
          :label="$t('GENERAL.LABELS.SEARCH')"
          class="top-header-search-input pt-0 mt-0 mx-2"
          single-line
          hide-details
          :value="searchValue"
          @input="onSearchInput"
          @keyup.enter="manualSearch && $emit('search-submit')"
        ></v-text-field>
        <slot name="top-header-actions">
          <v-spacer v-if="!manualSearch"></v-spacer>
        </slot>
        <v-btn
          v-if="filterable && manualSearch"
          small
          color="success"
          class="top-header-search-button white--text mb-1 mr-2"
          @click="$emit('search-submit')"
        >
          {{ $t("GENERAL.LABELS.SEARCH") }}
        </v-btn>
      </div>
      <v-spacer></v-spacer>
      <v-tooltip top color="primary">
        <template v-slot:activator="{ on, attrs }">
          <span v-bind="attrs" v-on="on" class="d-inline-flex">
            <v-btn
              icon
              color="indigo"
              :disabled="!hasCustomColumnOrder"
              @click="$emit('reset-columns')"
            >
              <v-icon>mdi-restore</v-icon>
            </v-btn>
          </span>
        </template>
        <span>{{ $t('GENERAL.TABLE.RESET_COLUMNS') }}</span>
      </v-tooltip>
      <v-btn
        icon
        color="indigo"
        @click="$emit('reset-column-widths')"
      >
        <v-tooltip top color="primary">
          <template v-slot:activator="{ on, attrs }">
            <v-icon
              v-bind="attrs"
              v-on="on"
            >
              mdi-table-column-width
            </v-icon>
          </template>
          <span>
            {{ $t('GENERAL.LABELS.RESET_COLUMN_WIDTHS') }}
          </span>
        </v-tooltip>
      </v-btn>
      <v-menu
        v-if="hiddenHeaders && hiddenHeaders.length"
        bottom
        left
        :close-on-content-click="false"
      >
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            icon
            color="indigo"
            v-bind="attrs"
            v-on="on"
          >
            <v-tooltip top color="primary">
              <template v-slot:activator="{ on, attrs }">
                <v-icon
                  v-bind="attrs"
                  v-on="on"
                >
                  mdi-format-list-bulleted-square
                </v-icon>
              </template>
              <span>
                {{ $t('GENERAL.LABELS.ADDITIONAL_COLUMNS') }}
              </span>
            </v-tooltip>
          </v-btn>
        </template>
        <v-card>
          <v-list class="mt-2 pb-0">
            <v-list-item>
              <v-autocomplete
                dense
                chips
                small-chips
                multiple
                solo
                return-object
                :value="localHiddenHeadersValues"
                :label="$t('GENERAL.LABELS.HEADERS')"
                :items="hiddenHeaders"
                item-value="value"
                item-text="text"
                @change="handleHiddenHeadersChange($event)"
              >
                <template v-slot:selection="data">
                  <v-chip
                    small
                    v-bind="data.attrs"
                    :input-value="data.selected"
                    close
                    @click:close="handleRemoveHiddenHeaderSelection(data.item)"
                  >
                    {{ data.item.text }}
                  </v-chip>
                </template>
              </v-autocomplete>
            </v-list-item>
          </v-list>
        </v-card>
      </v-menu>
      <v-menu
        v-if="filterable && filters && filters.length"
        bottom
        left
        max-height="85vh"
        min-width="360"
        content-class="table-filters-menu"
        :close-on-content-click="false"
      >
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            icon
            color="indigo"
            v-bind="attrs"
            v-on="on"
          >
            <v-tooltip top color="primary">
              <template v-slot:activator="{ on, attrs }">
                <v-icon
                  v-bind="attrs"
                  v-on="on"
                >
                  mdi-filter-variant
                </v-icon>
              </template>
              <span>
                {{ $t('GENERAL.LABELS.FILTERS') }}
              </span>
            </v-tooltip>
          </v-btn>
        </template>
        <v-card class="table-filters-menu-card">
          <v-list class="mt-2 pb-0 table-filters-menu-list">
            <v-list-item :key="index" v-for="(filterData, index) in filters">
              <v-text-field
                v-if="filterData.type === 'text'"
                dense
                :label="filterData.label"
                v-model="localFiltersValues[filterData.key]"
                clearable
                @input="handleTextFilterChange()"
              />
              <DatePicker
                v-else-if="filterData.type === 'date'"
                :label="filterData.label"
                v-model="localFiltersValues[filterData.key]"
                clearable
                :without-hint="true"
                :without-icon="true"
                class="filter-item"
                @input="handleFiltersChange"
              />
              <FieldTagsInput
                v-else-if="filterData.type === 'field_tag'"
                class="filter-item"
                dense
                solo
                flat
                hide-details
                value-mode="id"
                :value="localFiltersValues[filterData.key] || []"
                :field-tag-type-slug="filterData.field_tag_type_slug"
                :label="filterData.label"
                @input="handleFieldTagFilterChange(filterData.key, $event)"
                @loaded="handleFieldTagsLoaded(filterData, $event)"
              />
              <v-autocomplete
                v-else-if="filterData.type === 'select'"
                dense
                solo
                flat
                clearable
                hide-selected
                chips
                small-chips
                multiple
                class="filter-item"
                v-model="localFiltersValues[filterData.key]"
                :label="filterData.label"
                :items="filterData.items"
                :item-value="filterData['item-value']"
                :item-text="filterData['item-text']"
                :menu-props="{ maxHeight: 320, offsetY: true }"
                :filter="filterSelectItems"
                no-data-text="No matches"
                @change="handleFiltersChange"
              >
                <template v-slot:selection="data">
                  <v-chip
                    small
                    v-bind="data.attrs"
                    :input-value="data.selected"
                    close
                    @click:close="handleRemoveSelectFilterSelection(filterData.key, data.item[filterData['item-value']])"
                  >
                    {{ data.item[filterData['item-text']] }}
                  </v-chip>
                </template>
              </v-autocomplete>
              <FilterSearchAutocomplete
                v-else-if="filterData.type === 'tag_autocomplete' || filterData.type === 'mapping_autocomplete'"
                class="filter-item"
                :filter-data="filterData"
                :value="localFiltersValues[filterData.key] || []"
                @input="handleSearchFilterChange(filterData.key, $event)"
              />
              <ContactsAutocomplete
                v-else-if="filterData.type === 'company_contact'"
                class="filter-item"
                :value="localFiltersValues[filterData.key] || null"
                :not-required="true"
                :clearable="true"
                :show-add-new="false"
                @input="handleContactFilterChange(filterData.key, $event)"
              />
            </v-list-item>
          </v-list>
          <v-card-actions class="pt-0">
            <v-spacer></v-spacer>
            <v-btn
              text
              color="primary"
              @click="handleClearFilters"
            >
              Clear filters
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-menu>
    </v-card-title>
    <div class="filters-container" v-if="filters && filters.length">
      <span
        v-for="(filter, key) in localFiltersValues"
        v-if="isDisplayableFilterKey(key)"
        :key="'server-filters-' + key"
      >
        <v-chip
          v-if="filterLabels[key] && hasFilterValue(filter)"
          class="mx-1 my-1"
          :key="`${key} - labeled`"
          small
          close
          @click:close="handleRemoveFilterSelection(key)"
        >
          {{ filterLabels[key] }}
        </v-chip>
        <template v-else-if="Array.isArray(filter) && filter.length">
          <v-chip
            v-for="value in filter"
            class="mx-1 my-1"
            :key="`${key} - ${value}`"
            small
            close
            @click:close="handleRemoveSelectFilterSelection(key, value)"
          >
            {{ getFilterText(key, value) }}
          </v-chip>
        </template>
        <v-chip
          v-else-if="!Array.isArray(filter) && filter"
          class="mx-1 my-1"
          :key="`${key} - ${filter}`"
          small
          close
          @click:close="handleRemoveFilterSelection(key)"
        >
          {{ getFilterText(key, filter) }}
        </v-chip>
      </span>
    </div>
  </div>
</template>

<script>
import { debounce } from "lodash";
import { mapGetters } from "vuex";
import DatePicker from "@/views/partials/form/DatePicker.vue";
import FieldTagsInput from "@/views/partials/form/FieldTagsInput.vue";
import ContactsAutocomplete from "@/views/tagProcessor/Input/ContactsAutocomplete.vue";
import FilterSearchAutocomplete from "@/views/partials/v-table/components/FilterSearchAutocomplete.vue";
import { SEARCH_COMPANY_CONTACTS } from "@/store/companyContact.module";
import { GET_FIELD_TAG_OPTIONS } from "@/store/fieldTags.module";

export default {
  components: {
    DatePicker,
    FieldTagsInput,
    ContactsAutocomplete,
    FilterSearchAutocomplete
  },

  props: {
    filterable: {
      type: Boolean,
      default: false
    },

    filters: {
      type: Array,
      default: () => []
    },

    searchValue: {
      type: String,
      default: ''
    },

    filtersValues: {
      type: Object,
      default: () => ({})
    },

    filterLabels: {
      type: Object,
      default: () => ({})
    },

    hiddenHeaders: {
      type: Array,
      default: undefined
    },

    hiddenHeadersValues: {
      type: Array,
      default: () => []
    },

    hasCustomColumnOrder: {
      type: Boolean,
      default: false
    },

    manualSearch: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      localFiltersValues: this.filtersValues,
      localHiddenHeadersValues: this.hiddenHeadersValues
    };
  },

  computed: {
    ...mapGetters(["companyContacts"])
  },

  mounted() {
    if (this.filters.length) {
      this.initFiltersValues();
      this.preloadContactFilters();
      this.preloadFieldTagFilters();
    }
  },

  watch: {
    filters() {
      this.initFiltersValues();
      this.preloadFieldTagFilters();
    },

    filtersValues() {
      this.initFiltersValues();
    }
  },

  methods: {
    onSearchInput(value) {
      if (this.manualSearch) {
        this.$emit("search-draft", value);
        return;
      }

      this.$emit("search", value);
    },

    isDisplayableFilterKey(key) {
      return this.filters.some((filter) => filter.key === key);
    },

    initFiltersValues() {
      this.localFiltersValues = this.filtersValues;

      this.filters.forEach((filter) => {
        if (!this.localFiltersValues[filter.key]) {
          this.localFiltersValues[filter.key] = this.isMultiValueFilter(filter) ? [] : '';
        }
      });
    },

    isMultiValueFilter(filter) {
      return filter.type === 'select'
        || filter.type === 'field_tag'
        || filter.type === 'tag_autocomplete'
        || filter.type === 'mapping_autocomplete';
    },

    handleFiltersChange() {
      this.$emit('filters-change', this.localFiltersValues);
    },

    handleContactFilterChange(key, value) {
      this.$set(this.localFiltersValues, key, value || '');

      this.handleFiltersChange();
    },

    handleSearchFilterChange(key, value) {
      this.$set(this.localFiltersValues, key, value || []);

      this.handleFiltersChange();
    },

    handleFieldTagFilterChange(key, value) {
      this.$set(this.localFiltersValues, key, value || []);

      this.handleFiltersChange();
    },

    handleFieldTagsLoaded(filterData, tags) {
      this.$set(filterData, "items", tags || []);
    },

    preloadContactFilters() {
      this.filters
        .filter((filter) => filter.type === 'company_contact' && this.localFiltersValues[filter.key])
        .forEach((filter) => {
          this.$store.dispatch(SEARCH_COMPANY_CONTACTS, this.localFiltersValues[filter.key]);
        });
    },

    preloadFieldTagFilters() {
      this.filters
        .filter((filter) => filter.type === "field_tag" && filter.field_tag_type_slug)
        .forEach((filter) => {
          this.$store
            .dispatch(GET_FIELD_TAG_OPTIONS, {
              field_tag_type_slug: filter.field_tag_type_slug,
            })
            .then((tags) => {
              this.$set(filter, "items", (tags || []).map((tag) => ({
                id: tag.id,
                value: tag.value,
              })));
            });
        });
    },

    hasFilterValue(filter) {
      return Array.isArray(filter)
        ? filter.length > 0
        : filter !== undefined && filter !== null && filter !== '';
    },

    filterSelectItems(item, queryText, itemText) {
      if (!queryText) {
        return true;
      }

      const text = (itemText || '').toString().toLowerCase();
      const query = queryText.toString().toLowerCase();

      return text.indexOf(query) > -1;
    },

    getFilterText(key, value) {
      const filter = this.filters.find((filter) => filter.key === key);

      if (!filter) return '---';

      if (filter.type === 'select') {
        const filterValue = filter.items.find((item) => item[filter['item-value']] === value);

        return `${filter.label}: ${filterValue ? filterValue[filter['item-text']] : value}`;
      }

      if (filter.type === 'field_tag') {
        const filterValue = (filter.items || []).find((item) => Number(item.id) === Number(value));

        return `${filter.label}: ${filterValue ? filterValue.value : value}`;
      }

      if (filter.type === 'tag_autocomplete' || filter.type === 'mapping_autocomplete') {
        return `${filter.label}: ${value}`;
      }

      if (filter.type === 'company_contact') {
        const contact = (this.companyContacts || []).find((item) => item.id === value);
        const name = contact
          ? [contact.name, contact.company_name].filter(Boolean).join(', ')
          : value;

        return `${filter.label}: ${name}`;
      }

      return `${filter.label}: ${value}`;
    },

    handleTextFilterChange: debounce(function () {
      this.handleFiltersChange();
    }, 1500),

    handleRemoveSelectFilterSelection(filterKey, item) {
      const index = this.localFiltersValues[filterKey].indexOf(item);
      if (index >= 0) {
        this.localFiltersValues[filterKey].splice(index, 1);

        this.handleFiltersChange();
      }
    },

    handleRemoveFilterSelection(filterKey) {
      this.localFiltersValues[filterKey] = '';

      this.handleFiltersChange();
    },

    handleClearFilters() {
      const today = new Date().toISOString().slice(0, 10);

      this.filters.forEach((filter) => {
        if (Array.isArray(this.localFiltersValues[filter.key])) {
          this.localFiltersValues[filter.key].splice(0, this.localFiltersValues[filter.key].length);
        } else if (filter.type === 'date') {
          this.localFiltersValues[filter.key] = today;
        } else {
          this.localFiltersValues[filter.key] = '';
        }
      });

      this.handleFiltersChange();
    },

    handleHiddenHeadersChange(values) {
      this.localHiddenHeadersValues = values;

      this.$emit('headers-change', this.localHiddenHeadersValues);
    },

    handleRemoveHiddenHeaderSelection(item) {
      const index = this.localHiddenHeadersValues.findIndex(header => header.value === item.value);

      if (index >= 0) {
        this.localHiddenHeadersValues.splice(index, 1);

        this.$emit('headers-change', this.localHiddenHeadersValues);
      }
    }
  }
}
</script>

<style lang="scss" scoped>
:deep(.v-text-field__details) {
    margin-bottom: 0 !important;
}

:deep(.v-text-field) {
    padding-top: 0 !important;
}

.filters-container {
  padding: 0 16px;
}

.filter-item {
  width: 100%;
}

.top-header-search-input {
  max-width: 300px;
}

.top-header-search-inline {
  display: contents;
}

.top-header-search-group {
  display: flex;
  align-items: flex-end;
  flex: 0 1 auto;
  flex-wrap: wrap;
  max-width: 100%;
}

.top-header-search-group .top-header-search-input {
  flex: 0 0 300px;
  width: 300px;
  max-width: 300px;
}

.top-header-search-button {
  flex: 0 0 auto;
}

.top-header--manual-search {
  align-items: flex-end !important;
}

:deep(.table-filters-menu) {
  overflow: visible;
}

.table-filters-menu-card {
  min-height: 360px;
  overflow: visible;
}

.table-filters-menu-list {
  max-height: 70vh;
  overflow-y: auto;
}
</style>
